import { useQuery } from "@apollo/client/react";
import type { PlainDate, PlainDateString } from "@sa-nu/shared-utils/date";
import { toPlainDateString, getCurrentDate } from "@sa-nu/shared-utils/date";

import { graphql } from "../../../../../../__generated__/gql";
import type { buildAccommodationReservationProviderInput } from "../../../../../../utils";
import { deriveCheckoutWindow } from "../_utils/derive-checkout-window";

const CheckOutAvailabilitiesOnCheckoutDatePickerDrawerQuery = graphql(`
  query CheckOutAvailabilitiesOnCheckoutDatePickerDrawer(
    $input: AccommodationReservationCheckOutAvailabilitiesInput!
  ) {
    accommodationReservationCheckOutAvailabilities(input: $input) {
      items {
        date
        available
      }
    }
  }
`);

type UseCheckoutAvailabilitiesParams = {
  accommodationId: string;
  /** non-null の間だけ fetch する (CI 選択後に fetch) */
  firstNight: PlainDate | null;
  provider: ReturnType<typeof buildAccommodationReservationProviderInput>;
};

type UseCheckoutAvailabilitiesResult = {
  maxNights: number;
  coDisabledDates: Set<PlainDateString>;
  loading: boolean;
  error: Error | undefined;
};

/**
 * チェックアウト候補を fetch し、derive-checkout-window を通して返す。
 * firstNight が null の場合は fetch しない (skip)。
 * firstNight が変わると再 fetch する。
 */
export function useCheckoutAvailabilities({
  accommodationId,
  firstNight,
  provider,
}: UseCheckoutAvailabilitiesParams): UseCheckoutAvailabilitiesResult {
  const skip = firstNight === null;
  // skip=true の場合は実行されないため、現在日付をプレースホルダーとして使用
  const firstNightStr = toPlainDateString(firstNight ?? getCurrentDate());

  const { data, loading, error } = useQuery(
    CheckOutAvailabilitiesOnCheckoutDatePickerDrawerQuery,
    {
      skip,
      variables: {
        input: {
          accommodationId,
          firstNight: firstNightStr,
          provider,
        },
      },
    },
  );

  const payload = data?.accommodationReservationCheckOutAvailabilities;
  const items = payload?.items ?? [];
  // skip 時は Apollo が前回の data を保持していても必ず空の window を返す
  const { maxNights, coDisabledDates } = skip
    ? { maxNights: 0, coDisabledDates: new Set<PlainDateString>() }
    : deriveCheckoutWindow(
        items.flatMap((item) => {
          if (item.date === undefined) return [];
          return [{ date: item.date, available: item.available ?? false }];
        }),
      );

  return {
    maxNights,
    coDisabledDates,
    loading,
    error,
  };
}
